/**
 * Pure H OS · Sprint 3 · Marketing Tasks.
 * Composes Card, SectionHeader, Button, TextInput, TabBar, SeverityBadge,
 * ListRow, Field, Drawer.
 */
import { useMemo, useState } from "react";
import { CheckCircle2, Clock3, AlertCircle, ListChecks } from "lucide-react";
import { Card, SectionHeader, Button, TextInput, SeverityBadge } from "../../components/lib";
import { TabBar, ListRow, Drawer, Field } from "../../components/lib.overlay";
import { TASKS, TaskStatus, MktTask, CAMPAIGNS } from "../../mocks/marketingData";

const STATUS_LABEL: Record<TaskStatus, string> = {
  "todo": "To do", "in-progress": "In progress", "blocked": "Blocked", "done": "Done",
};
const STATUS_TINT: Record<TaskStatus, { bg: string; fg: string; icon: any }> = {
  "todo":        { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)",     icon: Clock3       },
  "in-progress": { bg: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)", icon: Clock3       },
  "blocked":     { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)",        icon: AlertCircle  },
  "done":        { bg: "var(--ph-success-tint)", fg: "var(--ph-success)",       icon: CheckCircle2 },
};

export default function Tasks() {
  const [tasks, setTasks] = useState<MktTask[]>(TASKS);
  const [tab, setTab] = useState<TaskStatus | "all">("todo");
  const [q, setQ]     = useState("");
  const [selected, setSelected] = useState<MktTask | null>(null);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: tasks.length };
    tasks.forEach((t) => { c[t.status] = (c[t.status] || 0) + 1; });
    return c;
  }, [tasks]);

  const filtered = useMemo(
    () => tasks.filter((t) =>
      (tab === "all" || t.status === tab) &&
      (!q || t.title.toLowerCase().includes(q.toLowerCase()) || t.owner.toLowerCase().includes(q.toLowerCase())),
    ),
    [tasks, tab, q],
  );

  const advance = (id: string) => {
    setTasks((list) => list.map((t) => {
      if (t.id !== id) return t;
      const order: TaskStatus[] = ["todo", "in-progress", "blocked", "done"];
      const next = order[(order.indexOf(t.status) + 1) % order.length];
      return { ...t, status: next };
    }));
    setSelected(null);
  };

  return (
    <div className="space-y-6" data-testid="mkt-tasks">
      <Card testId="mkt-tasks-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1"><TextInput value={q} onChange={setQ} placeholder="Search tasks…" testId="mkt-tasks-search" /></div>
          <Button testId="mkt-tasks-add">+ New task</Button>
        </div>
        <div className="mt-4">
          <TabBar<TaskStatus | "all">
            testId="mkt-tasks-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "todo",        label: "To do",       count: counts.todo        },
              { key: "in-progress", label: "In progress", count: counts["in-progress"] },
              { key: "blocked",     label: "Blocked",     count: counts.blocked     },
              { key: "done",        label: "Done",        count: counts.done        },
              { key: "all",         label: "All",         count: counts.all         },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="mkt-tasks-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader
            eyebrow="Queue"
            title={`${filtered.length} task${filtered.length === 1 ? "" : "s"}`}
            action={<ListChecks size={14} style={{ color: "var(--ph-ink-muted)" }} />}
          />
        </div>
        <div className="p-2" data-testid="mkt-tasks-list">
          {filtered.length === 0 ? (
            <div className="py-8 text-center text-sm" style={{ color: "var(--ph-ink-muted)" }}>Nothing here.</div>
          ) : filtered.map((t) => {
            const tint = STATUS_TINT[t.status];
            const Icon = tint.icon;
            const cmp = CAMPAIGNS.find((c) => c.id === t.campaignId);
            return (
              <ListRow
                key={t.id}
                testId={`mkt-task-${t.id}`}
                onClick={() => setSelected(t)}
                icon={
                  <span className="inline-flex items-center justify-center" style={{
                    width: 26, height: 26, borderRadius: "999px",
                    background: tint.bg, color: tint.fg,
                  }}>
                    <Icon size={13} />
                  </span>
                }
                label={
                  <span className="inline-flex items-center gap-2">
                    <span className="truncate">{t.title}</span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase flex-shrink-0" style={{ letterSpacing: "0.14em", background: tint.bg, color: tint.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {STATUS_LABEL[t.status]}
                    </span>
                  </span>
                }
                sublabel={<>{t.owner} · <span style={{ color: t.severity === "high" ? "var(--ph-danger)" : "var(--ph-ink-muted)" }}>{t.dueRelative}</span>{cmp && <> · {cmp.name.split(" · ")[0]}</>}</>}
                trailing={<SeverityBadge severity={t.severity} />}
              />
            );
          })}
        </div>
      </Card>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Task" testId="mkt-task-drawer" width={440}>
        {selected && (
          <div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.25rem", color: "var(--ph-ink-strong)" }} className="mb-3">
              {selected.title}
            </div>
            <Field label="Status" inline>
              <span className="px-2 py-0.5 text-[10px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: STATUS_TINT[selected.status].bg, color: STATUS_TINT[selected.status].fg, borderRadius: "var(--ph-radius-sm)" }}>
                {STATUS_LABEL[selected.status]}
              </span>
            </Field>
            <Field label="Owner" inline><span className="text-sm">{selected.owner}</span></Field>
            <Field label="Due" inline><span className="text-sm">{selected.dueRelative}</span></Field>
            <Field label="Severity" inline><SeverityBadge severity={selected.severity} /></Field>
            {selected.campaignId && (
              <Field label="Campaign" inline>
                <span className="text-sm">{CAMPAIGNS.find((c) => c.id === selected.campaignId)?.name}</span>
              </Field>
            )}
            <div className="pt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setSelected(null)} testId="mkt-task-cancel">Cancel</Button>
              <Button onClick={() => advance(selected.id)} testId="mkt-task-advance">Advance status</Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
